import { CommonModule } from '@angular/common';
import { Component, computed, input, model, output, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { IAttachment } from 'src/app/@core/models/model';
import { FileIconComponent } from 'src/app/@shared/components/file-icon/file-icon.component';
import { SearchBarComponent } from "src/app/@shared/components/search-bar/search-bar.component";
import { getFileType } from 'src/app/@shared/utils/utils';
import { SVG } from 'src/app/common/svgs';
import { DOCUMENT_MODEL } from 'src/app/contract-management/models/contract-management.model';
import { ChatAttachment, FILE_GROUP } from 'src/app/features/general-agent/models/types';
import { GeneralAgentService } from 'src/app/features/general-agent/services/general-agent.service';
import { AttachmentFilterChip, KNOWLEDGE_CATEGORY, resolveKnowledgeCategory } from './attachment-group-filter-types';

type AttachmentFilter = 'ALL' | FILE_GROUP | DOCUMENT_MODEL | string;

@Component({
    selector: 'app-attachment-group',
    imports: [
        CommonModule,
        ButtonComponent,
        TooltipModule,
        SkeletonModule,
        DialogComponent,
        IconComponent,
        SearchBarComponent,
        FormsModule,
        FileIconComponent
    ],
    templateUrl: './attachment-group.component.html'
})
export class AttachmentGroupComponent {

    attachments = input<ChatAttachment[]>([]);
    canRemove = input<boolean>(true);
    canOpen = input<boolean>(false);
    /** When true, attachments the user uploaded (FILE_GROUP.ATTACHED, or no group at all) can't be opened — used for the public shared-thread view. */
    restrictInputFiles = input<boolean>(false);
    getAttachmentSourceFn = input<((attachment: ChatAttachment) => string) | null>(null);
    removeAttachment = output<ChatAttachment>();
    openAttachment = output<IAttachment>();

    dialogMode = input<boolean>(false);
    dialogVisible = model<boolean>(false);
    loading = input<boolean>(false);

    /** If provided, opens the dialog and fetches all files for this thread when "See all" is clicked */
    threadId = input<string | null>(null);

    /** Chat/thread title, used to build the "Download All" zip filename */
    threadTitle = input<string>('');

    /** Filter to pre-select when the dialog opens */
    initialFilter = input<AttachmentFilter | null>(null);

    maxVisibleAttachments = input<number>(4);

    /** When true: fixed-width cards with horizontal scroll. When false: grid layout with wrap. */
    horizontalScroll = input<boolean>(false);

    /** When true: aligns content to the right (used for single-attachment human messages). */
    gridDirection = input<string>('ltr');

    // ── Lazy-load state ───────────────────────────────────────────────────────
    private generalAgentService = inject(GeneralAgentService);
    isFetching = signal(false);
    fetchedAttachments = signal<ChatAttachment[]>([]);

    /** Prefer fetched (full) attachments over the chip-level input once loaded */
    currentAttachments = computed(() => {
        const source = this.fetchedAttachments().length > 0 ? this.fetchedAttachments() : this.attachments();
        return source.map(a => {
            if (a.category || this.effectiveDocModel(a) !== DOCUMENT_MODEL.REPOSITORY) return a;
            const scope = (a as any).knowledgeScope ?? (a.attachmentData as any)?.scope;
            return { ...a, category: resolveKnowledgeCategory(scope, a.metadata) };
        });
    });

    // ── View helpers ──────────────────────────────────────────────────────────
    allAttachmentsForView = computed(() => {
        return this.currentAttachments().map(attachment => ({
            ...attachment,
            type: getFileType(attachment.name)
        }));
    });

    visibleAttachments = computed(() => {
        const allAttachments = this.allAttachmentsForView();
        return allAttachments.slice(0, this.maxVisibleAttachments());
    });

    remainingCount = computed(() => {
        const total = this.allAttachmentsForView().length;
        return total > this.maxVisibleAttachments() ? total - this.maxVisibleAttachments() : 0;
    });

    removeIcon = SVG.REMOVE;

    hasMoreAttachments = computed(() => this.remainingCount() > 0);

    onRemove(attachment: ChatAttachment) {
        this.removeAttachment.emit(attachment);
    }

    openAllAttachmentsDialog() {
        this.searchValue.set('');
        const filter = this.initialFilter();
        if (filter) {
            this.activeFilter.set(filter);
        } else {
            this.activeFilter.set('ALL');
        }
        this.dialogVisible.set(true);
        if (this.threadId()) {
            this.loadFilesForThread(this.threadId()!);
        }
        console.log(this.attachments())
    }

    private loadFilesForThread(id: string) {
        this.isFetching.set(true);
        this.generalAgentService.getThreadFiles(id).then(res => {
            if (res.success) {
                const mappedFiles: ChatAttachment[] = res.data.map((file: any) => {
                    const docModel = file.refDocModel ?? file.docModel;
                    return {
                        _id: file._id,
                        docModel,
                        name: file.filename,
                        isGenerated: file?.isGenerated,
                        type: this.mapStringToFileType(file.type),
                        attachmentData: file,
                        group: file.group,
                        category: docModel === DOCUMENT_MODEL.REPOSITORY
                            ? resolveKnowledgeCategory(file.knowledgeScope, file.metadata)
                            : undefined
                    };
                });
                this.fetchedAttachments.set(mappedFiles);
            }
            this.isFetching.set(false);
        });
    }

    private mapStringToFileType(type: string): FILE_TYPE {
        const normalizedType = (type || '').toLowerCase();

        switch (normalizedType) {
            case 'application/pdf': return FILE_TYPE.PDF;
            case 'application/msword':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return FILE_TYPE.WORD;
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return FILE_TYPE.EXCEL;
            case 'application/vnd.ms-powerpoint':
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                return FILE_TYPE.POWERPOINT;
            case 'text/html': return FILE_TYPE.HTML;
            case 'text/plain': return FILE_TYPE.TEXT;
            case 'text/csv':
            case 'application/csv': return FILE_TYPE.CSV;
            case 'text/markdown': return FILE_TYPE.MARKDOWN;
            case 'text/x-python':
            case 'application/x-python-code': return FILE_TYPE.PYTHON;
            case 'image/jpeg': return FILE_TYPE.JPEG;
            case 'image/png': return FILE_TYPE.PNG;
            case 'image/jpg': return FILE_TYPE.JPG;
        }

        // Extension-based fallback
        const extension = normalizedType.split('.').pop() || normalizedType;
        switch (extension) {
            case 'pdf': return FILE_TYPE.PDF;
            case 'doc': case 'docx': return FILE_TYPE.WORD;
            case 'xls': case 'xlsx': return FILE_TYPE.EXCEL;
            case 'ppt': case 'pptx': return FILE_TYPE.POWERPOINT;
            case 'html': case 'htm': return FILE_TYPE.HTML;
            case 'txt': return FILE_TYPE.TEXT;
            case 'csv': return FILE_TYPE.CSV;
            case 'md': return FILE_TYPE.MARKDOWN;
            case 'py': return FILE_TYPE.PYTHON;
            default: return FILE_TYPE.PDF;
        }
    }

    onDialogHide() {
        this.dialogVisible.set(false);
        this.activeFilter.set('ALL');
        this.searchValue.set('');
    }

    searchValue = signal('');

    onSearch(searchValue: string = '') {
        this.searchValue.set(searchValue);
    }

    formatFileSize(size: number | undefined): string {
        if (!size) return 'Unknown size';
        if (size < 1024) return size + ' B';
        if (size < 1000 * 1000) return (size / 1000).toFixed(1) + ' KB';
        return (size / (1000 * 1000)).toFixed(1) + ' MB';
    }

    activeFilter = signal<AttachmentFilter>('ALL');

    chips = input<AttachmentFilterChip[]>([
        { label: 'All', docModel: 'ALL' },
        { label: 'Uploaded Files', docModel: DOCUMENT_MODEL.ATTACHMENT as AttachmentFilter },
        { label: 'Documents', docModel: DOCUMENT_MODEL.CONTRACT as AttachmentFilter },
        { label: 'Project Knowledge', docModel: KNOWLEDGE_CATEGORY.PROJECT as AttachmentFilter },
        { label: 'Workspace Knowledge', docModel: KNOWLEDGE_CATEGORY.WORKSPACE as AttachmentFilter },
        { label: 'Helper Files', docModel: FILE_GROUP.HELPER as AttachmentFilter },
        { label: 'Generated Files', docModel: FILE_GROUP.OUTPUT as AttachmentFilter },
    ]);

    onFilterChange(source: AttachmentFilter) {
        this.activeFilter.set(source as AttachmentFilter);
    }

    isDownloadingAll = signal(false);

    activeFilterLabel = computed(() => this.chips().find(c => c.docModel === this.activeFilter())?.label ?? 'All');

    downloadAllInCategory() {
        const tid = this.threadId();
        const ids = this.filteredAttachments().map(a => a._id);
        if (!tid || ids.length === 0 || this.isDownloadingAll()) return;

        const chatName = this.threadTitle() || 'Chat';
        const fileName = `${chatName} - ${this.activeFilterLabel()}`.replace(/[<>:"/\\|?*]/g, '_') + '.zip';

        this.isDownloadingAll.set(true);
        this.generalAgentService.downloadThreadFilesZip(tid, ids, fileName).subscribe({
            next: (res) => {
                if (res.success && res.data?.url) {
                    const link = document.createElement('a');
                    link.href = res.data.url;
                    link.click();
                }
                this.isDownloadingAll.set(false);
            },
            error: () => this.isDownloadingAll.set(false)
        });
    }


    private effectiveDocModel(attachment: ChatAttachment): DOCUMENT_MODEL {
        return attachment.refDocModel ?? attachment.docModel;
    }

    filteredAttachments = computed(() => {
        const all = this.currentAttachments().map(attachment => ({
            ...attachment,
            type: getFileType(attachment.name) as FILE_TYPE
        }));
        const currentFilter = this.activeFilter();
        const search = this.searchValue();

        let filtered: ChatAttachment[] = [];

        if (currentFilter === 'ALL') {
            filtered = all as ChatAttachment[];
        } else if (currentFilter === FILE_GROUP.ATTACHED) {
            filtered = all.filter(a => a.group === FILE_GROUP.ATTACHED || !a.group);
        } else if (currentFilter === FILE_GROUP.HELPER || currentFilter === FILE_GROUP.OUTPUT) {
            filtered = all.filter(a => a.group === currentFilter);
        } else if (currentFilter === DOCUMENT_MODEL.ATTACHMENT) {
            filtered = all.filter(a =>
                this.effectiveDocModel(a) === DOCUMENT_MODEL.ATTACHMENT &&
                (a.group === FILE_GROUP.ATTACHED || !a.group) &&
                !(a.attachmentData as any)?.isGenerated
            );
        } else if (currentFilter === DOCUMENT_MODEL.CONTRACT || currentFilter === DOCUMENT_MODEL.REPOSITORY) {
            filtered = all.filter(a => this.effectiveDocModel(a) === currentFilter);
        } else if (typeof currentFilter === 'string' && currentFilter.length > 0) {
            // Custom category (e.g. generate-document: WORKSPACE_KNOWLEDGE, INTEGRATIONS)
            filtered = all.filter(a => a.category === currentFilter);
        }

        if (search && search.trim()) {
            filtered = filtered.filter(a =>
                a.name?.toLowerCase().includes(search.toLowerCase())
            );
        }

        const attachmentIdsSet = new Set<string>();
        filtered = filtered.filter(a => {
            if (attachmentIdsSet.has(a._id)) return false;
            attachmentIdsSet.add(a._id);
            return true;
        });

        return filtered;
    });

    getAttachmentSource(attachment: ChatAttachment & { isGenerated: boolean }): string {
        const customFn = this.getAttachmentSourceFn();
        if (customFn) {
            return customFn(attachment);
        }
        return this.defaultGetAttachmentSource(attachment);
    }

    private defaultGetAttachmentSource(attachment: ChatAttachment & { isGenerated: boolean }): string {
        // Prefer the group field (new messages always have it)
        if (attachment.group === FILE_GROUP.OUTPUT) return 'Generated files';
        if (attachment.group === FILE_GROUP.HELPER) return 'Helper files';

        // Fallback for ATTACHED group or when group is missing (old messages)
        if (attachment.group === FILE_GROUP.ATTACHED || !attachment.group) {
            const model = this.effectiveDocModel(attachment);
            if (model === DOCUMENT_MODEL.ATTACHMENT) return 'Uploaded files';
            if (model === DOCUMENT_MODEL.CONTRACT) return 'Documents';
            if (model === DOCUMENT_MODEL.REPOSITORY) {
                return attachment.category === KNOWLEDGE_CATEGORY.PROJECT
                    ? 'Project Knowledge'
                    : 'Workspace Knowledge';
            }
        }
        return 'Files';
    }

    formatFileType(type: FILE_TYPE) {
        switch (type) {
            case FILE_TYPE.PDF: return 'PDF';
            case FILE_TYPE.WORD:
            case FILE_TYPE.DOC: return 'Document';
            case FILE_TYPE.EXCEL: return 'Spreadsheet';
            case FILE_TYPE.POWERPOINT: return 'Presentation';
            case FILE_TYPE.HTML: return 'HTML';
            case FILE_TYPE.TEXT: return 'Text';
            case FILE_TYPE.CSV: return 'CSV';
            case FILE_TYPE.PYTHON: return 'Python';
            case FILE_TYPE.MARKDOWN: return 'Markdown';
            case FILE_TYPE.JPG:
            case FILE_TYPE.JPEG:
            case FILE_TYPE.PNG: return 'Image';
        }
        return 'Unknown';
    }

    onOpen(attachmentData: IAttachment) {
        this.openAttachment.emit(attachmentData);
    }

    private isInputFile(attachment: ChatAttachment): boolean {
        return !attachment.group || attachment.group === FILE_GROUP.ATTACHED;
    }

    isRestricted(attachment: ChatAttachment): boolean {
        return this.restrictInputFiles() && this.isInputFile(attachment);
    }

    isOpenable(attachment: ChatAttachment): boolean {
        return this.canOpen() && !this.isRestricted(attachment);
    }
}
